import React, { useEffect, useMemo, useState, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAPI } from "../../config/api";
import { PLAYER_HEADSHOT_FALLBACK } from "../../config/constants";

const clampNumber = (v) => (Number.isFinite(v) ? v : null);


const formatGmli = (v) => {
  const n = clampNumber(v);
  return n === null ? "—" : n.toFixed(2);
};

const formatIP = (v) => {
  const n = clampNumber(v);
  if (n === null) return "—";
  const innings = Math.floor(n);
  const partial = Math.round((n - innings) * 10);
  return `${innings}.${partial}`;
};

const formatIPFromOuts = (avgOuts) => {
  const n = clampNumber(avgOuts);
  if (n === null) return "—";
  const totalOuts = Math.round(n);
  const innings = Math.floor(totalOuts / 3);
  const partialOuts = totalOuts % 3;
  return `${innings}.${partialOuts}`;
};

const formatPct = (v) => {
  const n = clampNumber(v);
  return n === null ? "—" : n.toFixed(1);
};

const formatWar = (v) => {
  const n = clampNumber(v);
  return n === null ? "—" : n.toFixed(1);
};

const getRoleInfo = (gmli) => {
  const g = clampNumber(gmli) ?? 0;
  if (g >= 1.5) return { role: "High Leverage", color: "text-red-600", bg: "bg-red-50", border: "border-red-200", pill: "bg-red-50 text-red-700 ring-red-200" };
  if (g >= 1.0) return { role: "Setup", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", pill: "bg-blue-50 text-blue-700 ring-blue-200" };
  if (g >= 0.7) return { role: "Middle Relief", color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200", pill: "bg-gray-50 text-gray-700 ring-gray-200" };
  return { role: "Mop-up", color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", pill: "bg-slate-50 text-slate-700 ring-slate-200" };
};

const Pill = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${className}`}>
    {children}
  </span>
);

const Headshot = memo(function Headshot({ src, name }) {
  return (
    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
      <img
        src={src || PLAYER_HEADSHOT_FALLBACK}
        alt={name || "Player"}
        className="h-full w-full object-cover object-top"
        onError={(e) => {
          if (e.currentTarget.src !== PLAYER_HEADSHOT_FALLBACK) {
            e.currentTarget.src = PLAYER_HEADSHOT_FALLBACK;
          }
        }}
        loading="lazy"
      />
    </div>
  );
});

const PitcherRow = memo(function PitcherRow({ pitcher, index, variant, onClick }) {
  const gmli = clampNumber(pitcher.relief_gmli ?? pitcher.gmli) ?? 0;
  const role = getRoleInfo(gmli);

  if (variant === "bullpen") {
    return (
      <div
        onClick={onClick}
        className={`group flex flex-col gap-2 rounded-lg border p-3 transition-all cursor-pointer hover:shadow-sm ${role.bg} ${role.border}`}
      >
        <div className="flex items-center gap-2">
          <Headshot src={pitcher.img_url} name={pitcher.player_name} />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm text-gray-900 group-hover:text-blue-600 break-words">
              {pitcher.player_name || "Unknown Player"}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <Pill className={role.pill}>{role.role}</Pill>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <div className="text-gray-500">
            {pitcher.games ?? 0} G
          </div>
          <div className="flex items-center gap-3">
          <div className="text-center min-w-[40px]">
              <div className="font-semibold text-gray-900">{formatIP(pitcher.ip)}</div>
              <div className="text-[10px] text-gray-400">IP</div>
            </div>
            <div className="text-center min-w-[40px]">
              <div className="font-semibold text-gray-900">{formatWar(pitcher.war)}</div>
              <div className="text-[10px] text-gray-400">WAR</div>
            </div>
            <div className="text-center min-w-[40px]">
              <div className="font-semibold text-gray-900">{formatPct(pitcher.k_pct)}%</div>
              <div className="text-[10px] text-gray-400">K%</div>
            </div>
            <div className="text-center min-w-[40px]">
              <div className="font-semibold text-gray-900">{formatPct(pitcher.bb_pct)}%</div>
              <div className="text-[10px] text-gray-400">BB%</div>
            </div>
            <div className="text-center min-w-[48px]">
              <div className={`font-semibold ${role.color}`}>{formatGmli(gmli)}</div>
              <div className="text-[10px] text-gray-400">gmLI</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 rounded-xl border border-gray-100 bg-white p-3 transition-all hover:border-blue-200 hover:shadow-sm cursor-pointer"
    >
      <div className="w-6 text-center text-lg font-bold text-gray-200">{index}</div>

      <Headshot src={pitcher.img_url} name={pitcher.player_name} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-gray-900 group-hover:text-blue-600">
            {pitcher.player_name || "Unknown Player"}
          </div>
        </div>

        <div className="mt-0.5 text-xs text-gray-500">
          {pitcher.starts ?? 0} GS • {pitcher.games ?? 0} G
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="min-w-[50px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatIP(pitcher.ip)}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">IP</div>
        </div>
        <div className="min-w-[50px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatWar(pitcher.war)}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">WAR</div>
        </div>
        <div className="min-w-[50px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatPct(pitcher.k_pct)}%</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">K%</div>
        </div>
        <div className="min-w-[50px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatPct(pitcher.bb_pct)}%</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">BB%</div>
        </div>
        <div className="min-w-[60px] text-center">
          <div className="text-sm font-semibold text-gray-900">{formatIPFromOuts(pitcher.avg_outs_start)}</div>
          <div className="text-[10px] font-medium tracking-wide text-gray-400">IP/Start</div>
        </div>
      </div>
    </div>
  );
});

export const PitcherUtilization = ({ teamId, years }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [selectedYear, setSelectedYear] = useState(years?.[0] ?? null);
  const isInitializingRef = useRef(true);
  const skipNextEffectRef = useRef(false);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const controller = new AbortController();

    (async () => {
      setStatus("loading");
      try {
        const yearParam = selectedYear ? `?year=${selectedYear}` : "";
        const res = await fetchAPI(`/api/team/${teamId}/pitcher-utilization${yearParam}`, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        
        setData(res);

        if (isInitializingRef.current && !selectedYear && res?.available_years?.length) {
          isInitializingRef.current = false;
          const firstYear = res.available_years[0];
          skipNextEffectRef.current = true;
          if (!controller.signal.aborted) {
            setSelectedYear(firstYear);
          }
        } else {
          isInitializingRef.current = false;
        }

        if (!controller.signal.aborted) {
          setStatus("ready");
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error fetching pitcher utilization:", e);
          setStatus("error");
          isInitializingRef.current = false;
        }
      }
    })();

    return () => controller.abort();
  }, [teamId, selectedYear]);

  const availableYears = useMemo(() => data?.available_years || years || [], [data, years]);

  const { rotation, bullpen, totalGames } = useMemo(() => {
    const pitchers = data?.pitchers || [];
    if (!pitchers.length) return { rotation: [], bullpen: [], totalGames: 1 };

    const tg = pitchers[0]?.total_games ?? 1;
    const threshold = Math.ceil(tg * 0.1);

    const rot = pitchers
      .filter((p) => (p.starts ?? 0) >= threshold)
      .slice(0, 5);
    
    const bull = pitchers
      .filter((p) => (p.starts ?? 0) < threshold && (p.games ?? 0) >= 2)
      .sort((a, b) => {
        const aGmli = clampNumber(a.relief_gmli ?? a.gmli) ?? 0;
        const bGmli = clampNumber(b.relief_gmli ?? b.gmli) ?? 0;
        return bGmli - aGmli;
      });

    return {
      totalGames: tg,
      rotation: rot,
      bullpen: bull,
    };
  }, [data]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
      </div>
    );
  }

  if (status === "error") {
    return <div className="py-12 text-center text-sm text-gray-500">Couldn&apos;t load pitcher utilization.</div>;
  }

  if (!data?.pitchers?.length) {
    return <div className="py-12 text-center text-sm text-gray-500">No pitcher data available.</div>;
  }

  return (
    <div className="p-2 pl-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {availableYears.length > 0 ? (
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-gray-500">Year</div>
            <select
              value={selectedYear ?? ""}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableYears.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {rotation.length > 0 ? (
        <div className="mb-8">
          <div className="grid gap-2">
            {rotation.map((p, idx) => (
              <PitcherRow
                key={p.pitcher_id}
                pitcher={p}
                index={idx + 1}
                variant="rotation"
                onClick={() => navigate(`/player/${p.pitcher_id}`)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {bullpen.length > 0 ? (
        <div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {bullpen.map((p) => (
              <PitcherRow
                key={p.pitcher_id}
                pitcher={p}
                totalGames={totalGames}
                variant="bullpen"
                onClick={() => navigate(`/player/${p.pitcher_id}`)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
